/**
 * Mockup Generator funnel — derivation helpers (Phase 5).
 *
 * PURE functions over the EXISTING catalog data (no new endpoints): `productTypesOf`
 * derives the distinct Product Type facet from `client.listTemplates()`, used by
 * catalogTree.ts to build the Product→Brand→Model tree.
 *
 * Kept framework-free so the funnel's business rules are unit-tested without a DOM.
 */
import type { ProductTemplate } from "@genz/platform";
import { categoryLabel } from "../templates/templateLabels.js";

/** A Product Type step option — the human label plus the raw category slug it maps to. */
export interface ProductTypeOption {
  /** Human label (e.g. "Transparent Phone Cases"), from `display.category`. */
  label: string;
  /** Distinct key used to filter templates (the raw `display.category` value). */
  key: string;
}

/**
 * Distinct Product Types across the catalog (step 1). Keyed by the raw
 * `display.category` (== product_type) so we can filter templates by it later; the label
 * comes from the SHARED `categoryLabel` (canonical `PRODUCT_CATEGORIES` label first, then
 * an already-human value, then a humanized slug) so the tree node, the index rows and the
 * editor's Category dropdown all say "Phone case (TPU)" rather than three different things.
 * Blank categories are skipped. Sorted by label for a stable UI.
 */
export function productTypesOf(templates: ProductTemplate[]): ProductTypeOption[] {
  const byKey = new Map<string, string>();
  for (const t of templates) {
    const key = (t.display?.category ?? "").trim();
    if (!key) continue;
    if (!byKey.has(key)) byKey.set(key, categoryLabel(key));
  }
  return [...byKey.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
